// Phase C1B — JSON-LD structured data builders.
// Governed by ADR-0007 §14 (JSON-LD addendum) + ADR-0008 (§7A Public Entity Model).
//
// Pure & deterministic: build-time constants only (no Date/random/window/fetch) → prerender-ready.
// Single source of truth: entity identity from companyLegalIdentity.generated (← canonical COMPANY_*);
// URLs from seoData. NO new source of truth, NO hardcoded identity literals.
// FORBIDDEN here (never emit): Review, aggregateRating, Offer/price, empty sameAs, unverified geo.
//
// Scope: C1B.1 = Organization (sitewide) + WebSite (home). C1B.2 = WebPage (per indexable route)
// + BreadcrumbList (non-home indexable routes). WP-3.2 = LocalBusiness facet layered onto the
// Organization @id (Service Area Business; fact-gated; geo/logo/sameAs fail-closed).

import { COMPANY_LEGAL } from '../config/companyLegalIdentity.generated';
import { SEO_SITE_URL, seo } from './seoData';
import { INDEXABLE, BREADCRUMB_LABEL } from '../navigation/routeRegistry';
// WP-3.3 — Service Layer (SVC-2). No cycle: serviceProjection imports seoData/sabProfile, not jsonld.
import { buildService } from './serviceProjection';
import { findServiceByPath } from '../navigation/serviceCatalog';
// WP-3.2 — Service Area Business facts (single ratified source; fact-gated, fail-closed).
import {
  AREA_SERVED,
  OPENING_HOURS_24_7,
  SUPPORT_HOURS,
  AVAILABLE_LANGUAGES,
  LOGO_URL,
  SAME_AS,
} from './sabProfile';

// Stable entity @ids — ADR-0008 §7A: Entity ID ≠ URL (fragment identifiers, constant across
// slug/locale/route changes). Service/Place @ids are intentionally NOT minted here — they derive
// from the ratified taxonomy when later phases activate.
export const ORG_ID = `${SEO_SITE_URL}/#organization`;
export const WEBSITE_ID = `${SEO_SITE_URL}/#website`;

// INDEXABLE (path → seoData key) and BREADCRUMB_LABEL (path → static FR label) now derive from the
// single navigation/route registry (`navigation/routeRegistry`) — the same source that drives
// Header, Footer, and the sitemap. The registry yields the identical 8-route indexable set and the
// identical breadcrumb labels (sourced from the canonical public-nav labels, ADR-0007 §12.8); the
// legacy cross-canonical /mentions-legales and all noindex routes are excluded there. C1B output is
// unchanged by this consolidation.
const HOME_LABEL = 'Accueil';

// Organization — the single public identity entity, sourced exclusively from the generated legal
// identity. WP-3.2 layers the LocalBusiness facet onto this SAME @id (ADR-0009 P10 — one identity,
// layered types; NOT a second node) with the ratified Service-Area-Business facts (areaServed,
// 24/7 openingHoursSpecification, support hours + languages on the ContactPoint). geo is omitted
// (SAB); logo/sameAs are fail-closed (emitted only when ratified + supplied). No forbidden props.
export function buildOrganization() {
  const a = COMPANY_LEGAL.address;
  const node = {
    '@type': ['Organization', 'LocalBusiness'],
    '@id': ORG_ID,
    name: COMPANY_LEGAL.brand,
    legalName: `${COMPANY_LEGAL.legalName} ${COMPANY_LEGAL.legalForm}`,
    url: `${SEO_SITE_URL}/`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: a.street,
      postalCode: a.postalCode,
      addressLocality: a.city,
      addressCountry: a.country === 'France' ? 'FR' : a.country,
    },
    vatID: COMPANY_LEGAL.vat,
    identifier: [
      { '@type': 'PropertyValue', propertyID: 'SIREN', value: COMPANY_LEGAL.siren },
      { '@type': 'PropertyValue', propertyID: 'SIRET', value: COMPANY_LEGAL.siret },
      { '@type': 'PropertyValue', propertyID: 'RCS', value: COMPANY_LEGAL.rcs },
    ],
    // SAB service area — AdministrativeArea + City Place objects (no free-text; geo omitted).
    areaServed: AREA_SERVED.map((p) => ({ '@type': p.type, name: p.name })),
    // Business availability: 24/7.
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [...OPENING_HOURS_24_7.dayOfWeek],
      opens: OPENING_HOURS_24_7.opens,
      closes: OPENING_HOURS_24_7.closes,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      telephone: COMPANY_LEGAL.phone,
      email: COMPANY_LEGAL.email,
      // Telephone support hours — distinct from the 24/7 service availability.
      hoursAvailable: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [...SUPPORT_HOURS.dayOfWeek],
        opens: SUPPORT_HOURS.opens,
        closes: SUPPORT_HOURS.closes,
      },
      availableLanguage: [...AVAILABLE_LANGUAGES],
    },
  };
  // Fail-closed facts (omitted until ratified + supplied): logo, sameAs. geo intentionally omitted (SAB).
  if (LOGO_URL) node.logo = LOGO_URL;
  if (SAME_AS.length) node.sameAs = [...SAME_AS];
  return node;
}

// WebSite — home only; publisher references the Organization @id (no duplication of identity).
// No SearchAction (no public on-site search endpoint exists).
export function buildWebSite() {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: COMPANY_LEGAL.brand,
    url: `${SEO_SITE_URL}/`,
    inLanguage: 'fr-FR',
    publisher: { '@id': ORG_ID },
  };
}

// WebPage — one per indexable route. url/name from seoData (single source for URLs/titles).
// Relationships use stable @ids: isPartOf → WebSite (defined on home; valid cross-page @id ref),
// about/publisher → Organization (present in every page graph), breadcrumb → BreadcrumbList (same graph).
export function buildWebPage(path) {
  const entry = seo[INDEXABLE[path]];
  const url = entry.canonical;
  const node = {
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: entry.title,
    inLanguage: 'fr-FR',
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
  };
  if (path !== '/') node.breadcrumb = { '@id': `${url}#breadcrumb` };
  // Wave 2 — mirror the page meta description onto the WebPage entity (graph consistency; the same
  // factual string already emitted as <meta name="description">). Fail-closed: omit if absent.
  if (entry.description) node.description = entry.description;
  return node;
}

// BreadcrumbList — non-home indexable routes. Two levels (Accueil → {page}); WP-3.3 service pages
// nest under the Services hub (Accueil → Services → {Service}). Item URLs are stable canonicals;
// item names are Content (slug ≠ translation).
export function buildBreadcrumb(path) {
  const entry = seo[INDEXABLE[path]];
  const url = entry.canonical;
  const items = [{ '@type': 'ListItem', position: 1, name: HOME_LABEL, item: `${SEO_SITE_URL}/` }];
  if (findServiceByPath(path)) {
    items.push({ '@type': 'ListItem', position: 2, name: BREADCRUMB_LABEL['/services'], item: `${SEO_SITE_URL}/services` });
    items.push({ '@type': 'ListItem', position: 3, name: BREADCRUMB_LABEL[path], item: url });
  } else {
    items.push({ '@type': 'ListItem', position: 2, name: BREADCRUMB_LABEL[path], item: url });
  }
  return { '@type': 'BreadcrumbList', '@id': `${url}#breadcrumb`, itemListElement: items };
}

function normalizePath(pathname) {
  if (!pathname) return '/';
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname;
}

// Returns the single @graph for a route, or null when the route emits no JSON-LD
// (noindex / cross-canonical / non-public). Per indexable route: Organization (every page) +
// WebSite (home only) + WebPage (every page) + BreadcrumbList (non-home only).
export function graphForPath(pathname) {
  const path = normalizePath(pathname);
  if (!Object.prototype.hasOwnProperty.call(INDEXABLE, path)) return null;
  const nodes = [buildOrganization()];
  if (path === '/') nodes.push(buildWebSite());
  nodes.push(buildWebPage(path));
  if (path !== '/') nodes.push(buildBreadcrumb(path));
  // WP-3.3 — on a service route, add the Service entity node (provider → Organization, SAB areaServed).
  const svc = findServiceByPath(path);
  if (svc) nodes.push(buildService(svc));
  return { '@context': 'https://schema.org', '@graph': nodes };
}
